import React, { FunctionComponent, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { ScrollView, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Divider } from '../../atoms/divider/Divider'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { TextButton } from '../../atoms/textButton/TextButton'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { TaskListItem, TaskListItemProps } from '../../molecules/taskListItem/TaskListItem'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

export type TasksModuleProps = {
  items: TaskListItemProps[]
  isLoading?: boolean
}

const COLLAPSED_TASK_COUNT = 3

const TasksCard = styled.View(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundPrimary,
  borderRadius: spacing['20px'],
  borderWidth: 1,
  borderColor: colors.cardOutlinePrimary,
  overflow: 'hidden',
}))

const ModuleHeader = styled.View(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundSecondary,
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['24px'],
}))

const EmptyStateRow = styled.View(({ theme: { spacing } }) => ({
  alignItems: 'center',
  paddingVertical: spacing['32px'],
  paddingHorizontal: spacing['24px'],
}))

const LoadingState = styled.View(({ theme: { spacing } }) => ({
  alignItems: 'center',
  paddingVertical: spacing['32px'],
}))

const TaskRows = styled(ScrollView)({
  maxHeight: 640,
})

const ToggleContainer = styled.View(({ theme: { spacing } }) => ({
  alignItems: 'center',
  paddingVertical: spacing['16px'],
}))

export const TasksModule: FunctionComponent<TasksModuleProps> = ({ items, isLoading = false }) => {
  const { colors } = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const hasExpandableTasks = items.length > COLLAPSED_TASK_COUNT
  const visibleItems = hasExpandableTasks && !isExpanded ? items.slice(0, COLLAPSED_TASK_COUNT) : items

  return (
    <View testID={tID('TasksModule')}>
      <TasksCard testID={tID('TasksModule-card')}>
        <ModuleHeader>
          <Title
            text={<FormattedMessage defaultMessage='Tasks' description='Header for the tasks module' />}
            size={TitleSize.SMALL}
          />
        </ModuleHeader>
        <Divider />
        {isLoading ? (
          <LoadingState testID={tID('TasksModule-loading')}>
            <LoadingIndicator topPadding={0} />
          </LoadingState>
        ) : items.length === 0 ? (
          <EmptyStateRow testID={tID('TasksModule-emptyState')}>
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage="You're all caught up"
                  description='Empty state message when there are no outstanding tasks'
                />
              }
              size={BodyTextSize.DEFAULT}
              color={colors.textSecondary}
            />
          </EmptyStateRow>
        ) : (
          <>
            <TaskRows testID={tID('TasksModule-row-container')}>
              {visibleItems.map((item, index) => (
                <React.Fragment key={index}>
                  <TaskListItem {...item} />
                  {index < visibleItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </TaskRows>
            {hasExpandableTasks && (
              <>
                <Divider />
                <ToggleContainer>
                  <TextButton
                    testID={tID('TasksModule-toggle-button')}
                    style={{ alignSelf: 'center' }}
                    accessibilityState={{ expanded: isExpanded }}
                    aria-expanded={isExpanded}
                    text={
                      isExpanded ? (
                        <FormattedMessage
                          defaultMessage='View less'
                          description='Button to collapse the tasks module'
                        />
                      ) : (
                        <FormattedMessage defaultMessage='View more' description='Button to expand the tasks module' />
                      )
                    }
                    onPress={() => setIsExpanded((expanded) => !expanded)}
                  />
                </ToggleContainer>
              </>
            )}
          </>
        )}
      </TasksCard>
    </View>
  )
}
